"use client";

import { useRef, useState } from "react";
import { Check, LayoutGrid, Pipette, RefreshCw, Settings, Trash2, X } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import LomdaUploadFile from "../shared/lomdaUploadFile";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    HotspotTextPosition,
    ImageFill,
    MediaAspect,
    WalkthroughHotspot,
    WalkthroughImageStep,
    WalkthroughStep,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import {
    HOTSPOT_COLORS,
    createWalkthroughHotspot,
} from "../../../lib/blockRegistry";
import { calloutClasses, tailClasses } from "../../../lib/walkthrough";
import { MEDIA_ASPECTS, aspectLabel } from "../../../lib/mediaAspect";
import StepLinkSelect from "./stepLinkSelect";
import {
    LOMDA_DROPZONE_CLASS,
    LOMDA_DROPZONE_DRAG_CLASS,
} from "../shared/dropzone";
import { IMAGE_UPLOAD } from "../../../lib/uploadLimits";

interface Props {
    step: WalkthroughImageStep;
    steps: WalkthroughStep[];
    onUpdate: (patch: Partial<WalkthroughImageStep>) => void;
}

const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

const ASPECT_CLASS: Record<MediaAspect, string> = {
    auto: "",
    "16:9": "aspect-video",
    "4:3": "aspect-[4/3]",
    "1:1": "aspect-square",
};

const GRID_STYLE = {
    backgroundImage:
        "linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.08) 1px, transparent 1px)",
    backgroundSize: "10% 10%",
};

const POSITIONS: HotspotTextPosition[] = [
    "auto",
    "top",
    "bottom",
    "start",
    "end",
    "none",
];

export default function ImageStepEditor({ step, steps, onUpdate }: Props) {
    const imageRef = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [settingsFor, setSettingsFor] = useState<string | null>(null);
    const [showGrid, setShowGrid] = useState(false);
    const [draft, setDraft] = useState<{
        x: number;
        y: number;
        width: number;
        height: number;
    } | null>(null);

    const drag = useRef<{
        mode: "draw" | "move" | "resize";
        id?: string;
        startX: number;
        startY: number;
        orig?: { x: number; y: number; width: number; height: number };
    } | null>(null);

    const pct = (clientX: number, clientY: number) => {
        const rect = imageRef.current!.getBoundingClientRect();
        return {
            x: ((clientX - rect.left) / rect.width) * 100,
            y: ((clientY - rect.top) / rect.height) * 100,
        };
    };

    const updateHotspot = (id: string, patch: Partial<WalkthroughHotspot>) =>
        onUpdate({
            hotspots: step.hotspots.map((h) =>
                h.id === id ? { ...h, ...patch } : h
            ),
        });

    const removeHotspot = (id: string) => {
        onUpdate({ hotspots: step.hotspots.filter((h) => h.id !== id) });
        setSelected(null);
    };

    const capture = (e: React.PointerEvent) =>
        imageRef.current?.setPointerCapture(e.pointerId);

    const onBackgroundDown = (e: React.PointerEvent) => {
        const p = pct(e.clientX, e.clientY);
        drag.current = { mode: "draw", startX: p.x, startY: p.y };
        setSelected(null);
        setDraft({ x: p.x, y: p.y, width: 0, height: 0 });
        capture(e);
    };

    const startMove = (e: React.PointerEvent, h: WalkthroughHotspot) => {
        e.stopPropagation();
        setSelected(h.id);
        const p = pct(e.clientX, e.clientY);
        drag.current = {
            mode: "move",
            id: h.id,
            startX: p.x,
            startY: p.y,
            orig: { x: h.x, y: h.y, width: h.width, height: h.height },
        };
        capture(e);
    };

    const startResize = (e: React.PointerEvent, h: WalkthroughHotspot) => {
        e.stopPropagation();
        const p = pct(e.clientX, e.clientY);
        drag.current = {
            mode: "resize",
            id: h.id,
            startX: p.x,
            startY: p.y,
            orig: { x: h.x, y: h.y, width: h.width, height: h.height },
        };
        capture(e);
    };

    const onMove = (e: React.PointerEvent) => {
        const d = drag.current;
        if (!d) return;
        const p = pct(e.clientX, e.clientY);
        if (d.mode === "draw") {
            setDraft({
                x: Math.min(d.startX, p.x),
                y: Math.min(d.startY, p.y),
                width: Math.abs(p.x - d.startX),
                height: Math.abs(p.y - d.startY),
            });
        } else if (d.mode === "move" && d.id && d.orig) {
            updateHotspot(d.id, {
                x: clamp(d.orig.x + (p.x - d.startX), 0, 100 - d.orig.width),
                y: clamp(d.orig.y + (p.y - d.startY), 0, 100 - d.orig.height),
            });
        } else if (d.mode === "resize" && d.id && d.orig) {
            updateHotspot(d.id, {
                width: clamp(d.orig.width + (p.x - d.startX), 4, 100 - d.orig.x),
                height: clamp(
                    d.orig.height + (p.y - d.startY),
                    4,
                    100 - d.orig.y
                ),
            });
        }
    };

    const onUp = () => {
        const d = drag.current;
        drag.current = null;
        if (d?.mode === "draw" && draft) {
            if (draft.width >= 4 && draft.height >= 4) {
                const h = createWalkthroughHotspot(
                    draft.x,
                    draft.y,
                    draft.width,
                    draft.height
                );
                onUpdate({ hotspots: [...step.hotspots, h] });
                setSelected(h.id);
            }
            setDraft(null);
        }
    };

    if (!step.image) {
        return (
            <div className="mx-auto w-full max-w-3xl">
                <LomdaUploadFile
                    reuse="image"
                    reuseLayout="canvas"
                    options={{
                        ...IMAGE_UPLOAD,
                        className:
                            `${LOMDA_DROPZONE_CLASS} h-64 w-full rounded-xl`,
                        dragClassName: LOMDA_DROPZONE_DRAG_CLASS,
                        title: {
                            text: t("lomda-v3-editor.media.upload-image"),
                            keyName: "lomda-v3-editor.media.upload-image",
                        },
                        subtitle: {
                            text: t("lomda-v3-editor.media.drop-hint"),
                            keyName: "lomda-v3-editor.media.drop-hint",
                        },
                    }}
                    onUploadFinished={(file) => onUpdate({ image: file.fileUrl })}
                />
            </div>
        );
    }

    const interactive = step.interactive !== false;
    const aspect = step.aspect ?? "auto";
    const fit = step.fit ?? "cover";

    return (
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => onUpdate({ image: "", hotspots: [] })}
                    className="flex items-center gap-1.5 rounded-lg border bg-card px-2 py-1 lomda-v3-text-small text-muted-foreground transition hover:text-foreground"
                >
                    <RefreshCw className="size-3.5" />
                    {t("lomda-v3-editor.walkthrough.change-image")}
                </button>

                <input
                    value={step.alt ?? ""}
                    onChange={(e) => onUpdate({ alt: e.target.value })}
                    placeholder={t("lomda-v3-editor.walkthrough.alt-text")}
                    className="h-8 w-40 rounded-lg border px-2 lomda-v3-text-small outline-none"
                />

                <select
                    value={aspect}
                    onChange={(e) =>
                        onUpdate({ aspect: e.target.value as MediaAspect })
                    }
                    className="h-8 rounded-lg border bg-card px-2 lomda-v3-text-small outline-none"
                >
                    {MEDIA_ASPECTS.map((a) => (
                        <option key={a} value={a}>
                            {t("lomda-v3-editor.media.aspect")}: {aspectLabel(a)}
                        </option>
                    ))}
                </select>

                {aspect !== "auto" && (
                    <button
                        type="button"
                        onClick={() =>
                            onUpdate({
                                fit: (fit === "cover"
                                    ? "contain"
                                    : "cover") as ImageFill,
                            })
                        }
                        className="rounded-lg border bg-card px-2 py-1.5 lomda-v3-text-small text-muted-foreground transition hover:text-foreground"
                    >
                        {fit === "cover"
                            ? t("lomda-v3-editor.block.image.fill-cover")
                            : t("lomda-v3-editor.block.image.fill-contain")}
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => setShowGrid((g) => !g)}
                    title={t("lomda-v3-editor.walkthrough.grid")}
                    className={cn(
                        "flex size-8 items-center justify-center rounded-lg border bg-card transition",
                        showGrid
                            ? "border-primary text-primary"
                            : "text-muted-foreground hover:text-foreground"
                    )}
                >
                    <LayoutGrid className="size-4" />
                </button>
            </div>
            <span className="lomda-v3-text-small text-muted-foreground">
                {interactive
                    ? t("lomda-v3-editor.walkthrough.hotspot-draw-hint")
                    : t("lomda-v3-editor.walkthrough.hotspot-draw-hint-info")}
            </span>

            <div
                ref={imageRef}
                onPointerDown={onBackgroundDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                className={cn(
                    "relative cursor-crosshair touch-none select-none overflow-hidden rounded-xl border bg-muted",
                    aspect !== "auto" && ASPECT_CLASS[aspect]
                )}
            >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src={step.image}
                    alt={step.alt ?? ""}
                    className={cn(
                        "pointer-events-none select-none",
                        aspect === "auto"
                            ? "w-full"
                            : cn(
                                  "absolute inset-0 h-full w-full",
                                  fit === "cover"
                                      ? "object-cover"
                                      : "object-contain"
                              )
                    )}
                    draggable={false}
                />

                {showGrid && (
                    <div
                        className="pointer-events-none absolute inset-0 z-[5]"
                        style={GRID_STYLE}
                    />
                )}

                {step.hotspots.map((h) => (
                    <div
                        key={h.id}
                        onPointerDown={(e) => startMove(e, h)}
                        style={{
                            left: `${h.x}%`,
                            top: `${h.y}%`,
                            width: `${h.width}%`,
                            height: `${h.height}%`,
                            borderColor: h.color,
                            boxShadow:
                                selected === h.id
                                    ? `0 0 0 2px #fff, 0 0 0 4px ${h.color}`
                                    : `0 0 10px ${h.color}99`,
                        }}
                        className="absolute cursor-move rounded-md border-2 bg-primary/5"
                    >
                        {h.textPosition !== "none" && (
                            <div
                                style={{ borderColor: h.color }}
                                className={cn(
                                    "absolute z-10 w-44 rounded-lg border bg-card p-2 shadow-md",
                                    calloutClasses(h.textPosition)
                                )}
                            >
                                <textarea
                                    value={h.label}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                        updateHotspot(h.id, {
                                            label: e.target.value,
                                        })
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.walkthrough.hotspot-text"
                                    )}
                                    rows={2}
                                    className="w-full resize-none bg-transparent lomda-v3-text-small outline-none"
                                />
                                <span
                                    style={{
                                        backgroundColor: "#fff",
                                        borderColor: h.color,
                                    }}
                                    className={cn(
                                        "absolute size-2.5 rotate-45 border",
                                        tailClasses(h.textPosition)
                                    )}
                                />
                            </div>
                        )}

                        <Popover
                            open={settingsFor === h.id}
                            onOpenChange={(o) =>
                                setSettingsFor(o ? h.id : null)
                            }
                        >
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                        setSelected(h.id);
                                    }}
                                    title={t(
                                        "lomda-v3-editor.walkthrough.hotspot-settings"
                                    )}
                                    className="absolute -end-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border bg-card text-muted-foreground shadow transition hover:text-foreground"
                                >
                                    <Settings className="size-3.5" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="start"
                                className="w-64 p-3"
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="lomda-v3-text-label">
                                        {t(
                                            "lomda-v3-editor.walkthrough.hotspot-settings"
                                        )}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            aria-label={t(
                                                "lomda-v3-editor.block.delete"
                                            )}
                                            onClick={() => removeHotspot(h.id)}
                                            className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:text-destructive"
                                        >
                                            <Trash2 className="size-4" />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="close"
                                            onClick={() => setSettingsFor(null)}
                                            className="flex size-6 items-center justify-center rounded text-muted-foreground transition hover:text-foreground"
                                        >
                                            <X className="size-4" />
                                        </button>
                                    </div>
                                </div>

                                {interactive && (
                                    <div className="mb-3 flex flex-col gap-1">
                                        <span className="lomda-v3-text-small text-muted-foreground">
                                            {t(
                                                "lomda-v3-editor.walkthrough.link-to"
                                            )}
                                        </span>
                                        <StepLinkSelect
                                            steps={steps}
                                            currentStepId={step.id}
                                            value={h.linkStepId}
                                            onChange={(linkStepId) =>
                                                updateHotspot(h.id, {
                                                    linkStepId,
                                                })
                                            }
                                        />
                                    </div>
                                )}

                                <div className="mb-3 flex flex-col gap-1">
                                    <span className="lomda-v3-text-small text-muted-foreground">
                                        {t(
                                            "lomda-v3-editor.walkthrough.text-position"
                                        )}
                                    </span>
                                    <select
                                        value={h.textPosition}
                                        onChange={(e) =>
                                            updateHotspot(h.id, {
                                                textPosition: e.target
                                                    .value as HotspotTextPosition,
                                            })
                                        }
                                        className="h-8 w-full rounded-lg border bg-card px-2 lomda-v3-text-small outline-none"
                                    >
                                        {POSITIONS.map((p) => (
                                            <option key={p} value={p}>
                                                {t(
                                                    `lomda-v3-editor.walkthrough.pos-${p}`
                                                )}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <span className="lomda-v3-text-small text-muted-foreground">
                                        {t(
                                            "lomda-v3-editor.walkthrough.highlight-color"
                                        )}
                                    </span>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {HOTSPOT_COLORS.map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() =>
                                                    updateHotspot(h.id, {
                                                        color: c,
                                                    })
                                                }
                                                style={{ backgroundColor: c }}
                                                className={cn(
                                                    "flex size-6 items-center justify-center rounded-full border-2 transition",
                                                    h.color === c
                                                        ? "border-foreground"
                                                        : "border-transparent"
                                                )}
                                            >
                                                {h.color === c && (
                                                    <Check className="size-3.5 text-primary-foreground" />
                                                )}
                                            </button>
                                        ))}
                                        <label
                                            title={t(
                                                "lomda-v3-editor.walkthrough.custom-color"
                                            )}
                                            className="relative flex size-6 cursor-pointer items-center justify-center rounded-full border text-muted-foreground"
                                        >
                                            <Pipette className="size-3.5" />
                                            <input
                                                type="color"
                                                value={h.color}
                                                onChange={(e) =>
                                                    updateHotspot(h.id, {
                                                        color: e.target.value,
                                                    })
                                                }
                                                className="absolute inset-0 cursor-pointer opacity-0"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>

                        {selected === h.id && (
                            <span
                                onPointerDown={(e) => startResize(e, h)}
                                style={{ backgroundColor: h.color }}
                                className="absolute -bottom-1.5 -end-1.5 size-3 cursor-nwse-resize rounded-full border-2 border-white"
                            />
                        )}
                    </div>
                ))}

                {draft && (
                    <div
                        style={{
                            left: `${draft.x}%`,
                            top: `${draft.y}%`,
                            width: `${draft.width}%`,
                            height: `${draft.height}%`,
                            borderColor: HOTSPOT_COLORS[0],
                        }}
                        className="absolute rounded-md border-2 border-dashed bg-primary/5"
                    />
                )}
            </div>
        </div>
    );
}
